import React from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { Form, Row, Col, Button } from 'react-bootstrap';
import type { FormValues } from '../CustomerWizard';

const DirectorsStep: React.FC = () => {
  const { register, formState: { errors } } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray({
    name: 'partyInfo.directors'
  });

  const handleAddDirector = () => {
    append({
      firstName: '',
      lastName: '',
      idNumber: '',
      nationality: '',
      dateOfBirth: '',
      designation: '',
      ownershipPercentage: 0,
      isExecutive: false,
      isCommunicationContact: false
    });
  };

  return (
    <div className="directors-step">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Company Directors</h4>
        <Button variant="outline-primary" onClick={handleAddDirector}>
          <i className="fa fa-plus me-2"></i>
          Add Director
        </Button>
      </div>

      {fields.map((field, index) => (
        <div key={field.id} className="card mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="card-title mb-0">Director {index + 1}</h5>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => remove(index)}
              >
                <i className="fa fa-trash"></i>
              </Button>
            </div>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    {...register(`partyInfo.directors.${index}.firstName`)}
                    className={errors.partyInfo?.directors?.[index]?.firstName ? 'is-invalid' : ''}
                  />
                  {errors.partyInfo?.directors?.[index]?.firstName && (
                    <Form.Control.Feedback type="invalid">
                      {errors.partyInfo.directors[index]?.firstName?.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    {...register(`partyInfo.directors.${index}.lastName`)}
                    className={errors.partyInfo?.directors?.[index]?.lastName ? 'is-invalid' : ''}
                  />
                  {errors.partyInfo?.directors?.[index]?.lastName && (
                    <Form.Control.Feedback type="invalid">
                      {errors.partyInfo.directors[index]?.lastName?.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ID Type *</Form.Label>
                  <Form.Select
                    {...register(`partyInfo.directors.${index}.idType` as const)}
                    className={errors.partyInfo?.directors?.[index]?.idType ? 'is-invalid' : ''}
                  >
                    <option value="">Select ID Type</option>
                    <option value="SA_ID">SA ID</option>
                    <option value="PASSPORT">Passport</option>
                  </Form.Select>
                  {errors.partyInfo?.directors?.[index]?.idType && (
                    <Form.Control.Feedback type="invalid">
                      {(errors.partyInfo.directors[index]?.idType as any)?.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ID Number</Form.Label>
                  <Form.Control
                    type="text"
                    {...register(`partyInfo.directors.${index}.idNumber`)}
                    className={errors.partyInfo?.directors?.[index]?.idNumber ? 'is-invalid' : ''}
                  />
                  {errors.partyInfo?.directors?.[index]?.idNumber && (
                    <Form.Control.Feedback type="invalid">
                      {errors.partyInfo.directors[index]?.idNumber?.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nationality</Form.Label>
                  <Form.Select
                    {...register(`partyInfo.directors.${index}.nationality`)}
                    className={errors.partyInfo?.directors?.[index]?.nationality ? 'is-invalid' : ''}
                  >
                    <option value="">Select Nationality</option>
                    <option value="SOUTH_AFRICA">South Africa</option>
                    <option value="NAMIBIA">Namibia</option>
                    <option value="ZIMBABWE">Zimbabwe</option>
                    <option value="UNITED_KINGDOM">United Kingdom</option>
                    <option value="OTHER">Other</option>
                  </Form.Select>
                  {errors.partyInfo?.directors?.[index]?.nationality && (
                    <Form.Control.Feedback type="invalid">
                      {errors.partyInfo.directors[index]?.nationality?.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date of Birth</Form.Label>
                  <Form.Control
                    type="date"
                    {...register(`partyInfo.directors.${index}.dateOfBirth`)}
                    className={errors.partyInfo?.directors?.[index]?.dateOfBirth ? 'is-invalid' : ''}
                  />
                  {errors.partyInfo?.directors?.[index]?.dateOfBirth && (
                    <Form.Control.Feedback type="invalid">
                      {errors.partyInfo.directors[index]?.dateOfBirth?.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Gender</Form.Label>
                  <Form.Select
                    {...register(`partyInfo.directors.${index}.gender` as const)}
                    className={errors.partyInfo?.directors?.[index]?.gender ? 'is-invalid' : ''}
                  >
                    <option value="">Select Gender</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                    <option value="PREFER_NOT-TO-SAY">Prefer not to say</option>
                  </Form.Select>
                  {errors.partyInfo?.directors?.[index]?.gender && (
                    <Form.Control.Feedback type="invalid">
                      {(errors.partyInfo.directors[index]?.gender as any)?.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ownership Percentage</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    {...register(`partyInfo.directors.${index}.ownershipPercentage`, { valueAsNumber: true })}
                    className={errors.partyInfo?.directors?.[index]?.ownershipPercentage ? 'is-invalid' : ''}
                  />
                  {errors.partyInfo?.directors?.[index]?.ownershipPercentage && (
                    <Form.Control.Feedback type="invalid">
                      {errors.partyInfo.directors[index]?.ownershipPercentage?.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Appointment Date</Form.Label>
                  <Form.Control
                    type="date"
                    {...register(`partyInfo.directors.${index}.appointmentDate` as const)}
                    className={errors.partyInfo?.directors?.[index]?.appointmentDate ? 'is-invalid' : ''}
                  />
                  {errors.partyInfo?.directors?.[index]?.appointmentDate && (
                    <Form.Control.Feedback type="invalid">
                      {(errors.partyInfo.directors[index]?.appointmentDate as any)?.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Designation</Form.Label>
                  <Form.Control
                    type="text"
                    {...register(`partyInfo.directors.${index}.designation`)}
                    className={errors.partyInfo?.directors?.[index]?.designation ? 'is-invalid' : ''}
                  />
                  {errors.partyInfo?.directors?.[index]?.designation && (
                    <Form.Control.Feedback type="invalid">
                      {errors.partyInfo.directors[index]?.designation?.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Race</Form.Label>
                  <Form.Select
                    {...register(`partyInfo.directors.${index}.race` as const)}
                    className={errors.partyInfo?.directors?.[index]?.race ? 'is-invalid' : ''}
                  >
                    <option value="">Select Race</option>
                    <option value="BLACK">Black</option>
                    <option value="COLOURED">Coloured</option>
                    <option value="WHITE">White</option>
                    <option value="ASIAN">Asian</option>
                    <option value="OTHER">Other</option>
                    <option value="PREFER_NOT-TO-SAY">Prefer not to say</option>
                  </Form.Select>
                  {errors.partyInfo?.directors?.[index]?.race && (
                    <Form.Control.Feedback type="invalid">
                      {(errors.partyInfo.directors[index]?.race as any)?.message}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Executive Director"
                    {...register(`partyInfo.directors.${index}.isExecutive`)}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Person with disability"
                    {...register(`partyInfo.directors.${index}.isDisabled` as const)}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Previously disadvantaged individual"
                    {...register(`partyInfo.directors.${index}.isPreviouslyDisadvantaged` as const)}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Nominated for communication"
                    {...register(`partyInfo.directors.${index}.isCommunicationContact` as const)}
                  />
                  <Form.Text className="text-muted">
                    Notifications are addressed to the nominated directors. Each nominated
                    director receives their own SMS; one email addresses all of them.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>
      ))}

      {fields.length === 0 && (
        <div className="text-center py-5">
          <i className="fa fa-users text-muted" style={{ fontSize: '3rem' }}></i>
          <p className="mt-3 text-muted">No directors added yet</p>
          <Button variant="outline-primary" onClick={handleAddDirector}>
            <i className="fa fa-plus me-2"></i>
            Add Director
          </Button>
        </div>
      )}
    </div>
  );
};

export default DirectorsStep; 